import { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import PersonalityRadarChart from './PersonalityRadarChart';
import PersonaImage from './PersonaImage';
import styles from './EgoCardHorizontal.module.css';

const EgoLogo = () => (
  <svg viewBox="0 0 100 115.47" className={styles.cardLogo}>
    <defs>
      <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#FFD700', stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: '#FFA500', stopOpacity: 1 }} />
      </linearGradient>
    </defs>
    <path
      fill="url(#goldGradient)"
      d="M50,0L93.3,25v50L50,100L6.7,75v-50L50,0z M50,10l-34.6,20v40L50,90l34.6-20v-40L50,10z"
    />
    <circle cx="50" cy="45" r="15" fill="url(#goldGradient)" />
    <path
      fill="url(#goldGradient)"
      d="M50,65 C40,65 35,55 35,55 C35,55 40,75 50,75 C60,75 65,55 65,55 C65,55 60,65 50,65z"
    />
  </svg>
);

const EgoCardHorizontal = forwardRef(
  ({ publicProfile, digitalLegacy, userStatus }, ref) => {
    if (!publicProfile || !digitalLegacy) {
      return null;
    }

    const isProfileOfMe = !!publicProfile.inheritsFrom;
    const name = digitalLegacy.personaName;
    const summary = isProfileOfMe
      ? publicProfile.overrides?.summary
      : publicProfile.summary;
    const imagePersonaId = isProfileOfMe
      ? publicProfile.inheritsFrom
      : publicProfile.personaId;
    const publicUrl = `https://app.egoprotocol.ai/p/${publicProfile.username}`;

    const isVerified = userStatus?.verificationStatus?.hasCompletedFullTest;
    const isPro =
      userStatus?.planTier === 'pro' ||
      userStatus?.planTier === 'founder' ||
      userStatus?.planTier === 'vip';

    const generateEgoId = (userId = '') => {
      const parts = userId
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(-12);
      if (parts.length < 12) return 'EGO-ID-NOT-AVAILABLE';
      return `EGO-${parts.slice(0, 4)}-${parts.slice(4, 8)}-${parts.slice(8, 12)}`;
    };

    return (
      <div className={styles.cardWrapper} ref={ref}>
        <div className={styles.cardBackground}></div>
        <div className={styles.cardContent}>
          <div className={styles.leftPanel}>
            <PersonaImage
              personaId={imagePersonaId}
              alt={name}
              className={styles.avatar}
              isOwner={true}
            />
            <div className={styles.nameSection}>
              <h3 className={styles.name}>{name}</h3>
              <p className={styles.tagline}>{summary}</p>
              <p className={styles.egoId}>
                {generateEgoId(digitalLegacy.userId)}
              </p>
            </div>
            <div className={styles.logoSection}>
              <EgoLogo />
              <span>EGO PROTOCOL</span>
            </div>
          </div>
          <div className={styles.rightPanel}>
            <div className={styles.radarContainer}>
              <PersonalityRadarChart
                personalityData={digitalLegacy.personalityData}
              />
            </div>
            <div className={styles.verificationSection}>
              <span className={styles.verifiedBy}>
                Endorsed by the Digital Passport of {digitalLegacy.personaName}
              </span>
              <div className={styles.badgeContainer}>
                {isVerified && (
                  <div className={`${styles.badge} ${styles.gold}`}>
                    Psychometrically Verified
                  </div>
                )}
                {isPro && (
                  <div className={`${styles.badge} ${styles.proBadge}`}>
                    VIP Member
                  </div>
                )}
              </div>
            </div>
            <div className={styles.qrSection}>
              <div className={styles.qrCode}>
                <QRCodeSVG
                  value={publicUrl}
                  size={100}
                  bgColor={'#ffffff'}
                  fgColor={'#161B22'}
                  level={'Q'}
                />
              </div>
              <div className={styles.urlInfo}>
                <span>Scan to Interact</span>
                <p>
                  {publicUrl.replace(/https?:\/\//, '').split('/p/')[0]}/p/
                  <br />
                  <strong>{publicProfile.username}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

EgoCardHorizontal.displayName = 'EgoCardHorizontal';

export default EgoCardHorizontal;
